import "./security-tab.scss";

import * as React from "react";

import {faCheck}         from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {cx}              from "emotion";

import {cst, cstAssign} from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {PasswordInput}  from "@Common/ui-components/password-input/password-input";
import {values}         from "@Common/utils/objects";

export const SecurityTab = ({
    branch,
    disabled,
    hasUnsavedChangesStatic : {set: setHasUnsavedChanges},
    onSave                  : emitSave,
    resetCallbackSetter     : setResetCallback,
}:Types.Xpresso.Account.Admin.OfficeManagement.ManagementModal.TabComponentProps) => cst<{
    apis            : Types.Apis.Loaders.XpressoLoadApis,
    fieldSetter     : (path:"password" | "matchPassword", value: string) => void,
    formState       : Types.Common.React.UseState.UseStateNext<{password: string, matchPassword: string}>,
    userLoad        : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.UserProfile.GetUser.Response>,
    validation      : {[name:string]: boolean},
    validationLines : Array<{message: string, validated: boolean}>,
}>(
    consumeContext("apis"),
    cstAssign("userLoad", ({apis}, next) => Load2({
        next,
        _key  : branch?.idBranch,
        fetch : () => branch ? apis.next.userProfile.getUserProfile(branch.idUser) : Promise.resolve(null),
    })),
    cstAssign("formState", ({}, next) => UseState({next, initValue: {password: "", matchPassword: ""}})),
    ({formState}, next) => next(
        setResetCallback(() => formState.onChange({password: "", matchPassword: ""}))
    ),
    cstAssign("fieldSetter", ({formState}, next) => next(
        (path, value) => {
            setHasUnsavedChanges(true);
            formState.onChange({...formState.value, [path]: value});
        }
    )),
    cstAssign("validation", ({formState}, next) => next({
        length: formState.value?.password?.length >= 6,
        number: /\d/.test(formState.value?.password),
        lowercase: formState.value?.password && /[a-z]/.test(formState.value?.password),
        uppercase: /[A-Z]/.test(formState.value?.password),
        special: /[@$!%*#?&]/.test(formState.value?.password),
        matches: formState.value?.password && formState.value?.password === formState.value?.matchPassword,
        full: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/.test(formState.value?.password) && formState.value?.password === formState.value?.matchPassword
    })),
    cstAssign("validationLines", ({validation}, next) => next(getValidationLines({validation}))),
    ({
        apis,
        fieldSetter : setFormField,
        formState   : {value: formValue},
        userLoad    : {loading, value: user},
        validation,
        validationLines,
    }) => (
        <div className="security-tab-2x1" xd-qa-type="card" xd-qa-name="Security">
            <header xd-qa-type="label" xd-qa-name="Security">Security</header>
            
            <section>
                <div className="field">
                    <label xd-qa-type="label" xd-qa-name="Username">Username</label>
                    <span className="value" xd-qa-type="text" xd-qa-name="Username">{!user || loading ? "Loading..." : user?.username}</span>
                </div>

                <div className="field">
                    <label xd-qa-type="label" xd-qa-name="Enter a new password">Enter a new password</label>
                    <PasswordInput
                        inputProps={{
                            disabled : disabled || loading,
                            onChange : (e) => setFormField("password", e.currentTarget.value),
                            value    : formValue.password,
                            "xd-qa-name": "Password",
                        }}
                        
                    />
                    <div className="validation-card">
                        {validationLines.map((line, i) => (
                            <div className="validation-line" key={i} xd-qa-type="label" xd-qa-name={line.message}>
                                {line.validated ? (
                                    <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                                ) : (
                                    <div className="validation-spacer"/>
                                )}
                                <div className={cx("validation-label", {validated: line.validated})}>{line.message}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="field">
                    <label xd-qa-type="label" xd-qa-name="Confirm new password">Confirm new password</label>
                    <PasswordInput
                        inputProps={{
                            disabled : disabled || loading,
                            onChange : (e) => setFormField("matchPassword", e.currentTarget.value),
                            value    : formValue.matchPassword,
                            "xd-qa-name": "Confirm Password",
                        }}
                    />
                    <div className="validation-card">
                        <div className="validation-line" xd-qa-type="label" xd-qa-name="Passwords Match">
                            {validation.matches ? (
                                <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                            ) : (
                                <div className="validation-spacer"/>
                            )}
                            <div className={cx("validation-label", {validated: validation.matches})}>Passwords Match</div>
                        </div>
                    </div>
                </div>
            </section>

            <button
                disabled={disabled || loading || !values(validation).every(Boolean)}
                onClick={() => emitSave(apis.next.userProfile.updateUserProfile({userId: user.id, password: formValue.password}).then())}
                xd-qa-type="button"
                xd-qa-name="Save"
            >
                Save
            </button>
        </div>
    )
);

const getValidationLines = ({validation}) => ([
    {
        "message": "At least 6 characters", 
        "validated": validation.length, 
    },
    {
        "message": "At least 1 number", 
        "validated": validation.number, 
    },
    {
        "message": "At least 1 lowercase letter", 
        "validated": validation.lowercase, 
    },
    {
        "message": "At least 1 uppercase letter", 
        "validated": validation.uppercase, 
    },
    {
        "message": "At least 1 special character @$!%*#?&", 
        "validated": validation.special, 
    },
]);

